/* eslint-disable react/prop-types */
/**
 * CartProvide
 * Contain most logic handing with Omnitech Api
 */

import _ from 'lodash'
import React from 'react'
import { createPersistedState } from '../../cache'
import CartContext from './cart-context'
import { createCart, createIsolateCart, fetchCart, updateCart, checkout } from './cart-api'

const useCartIdsState = createPersistedState('cartIds')

export default function CartProvider({ children }) {

  const [cartIds, setCartIds] = useCartIdsState({})

  function handleResetCart() {
    setCartIds({})
  }

  async function handleCreateCart(options = {}) {
    const {
      commerceChannel,
      type,
      orderMethodMandatory,
      payload,
      updateCartActions = [],
    } = options
    try {
      const createCartOptions = {
        payload,
        commerceChannel,
        type,
        orderMethodMandatory,
        ...(
          _.isEmpty(updateCartActions)
            ? {}
            : {
              params: {
                includes: ['cart_shipment_ids']
              }
            }
        ),
      }
      const { cart: newCart } = await createCart(createCartOptions)
      if (_.isEmpty(updateCartActions)) {
        const data = await handleFetchCart({
          ..._.omit(options, ['payload', 'params', 'updateCartActions']),
          ..._.get(options, 'params', {}),
          cartId: _.get(newCart, 'id'),
        })
        return data
      }
      // apply new cart shipment id to actions
      const actions = _.map(updateCartActions, (action) => {
        const { actionType, id } = action
        if (
          actionType === 'update_cart_shipment'
          && _.isEmpty(id)
        ) {
          return {
            ...action,
            id: _.get(newCart, 'cartShipmentIds.0'),
          }
        }
        return action
      })
      const data = await handleUpdateCart({
        ..._.omit(options, ['payload', 'updateCartActions']),
        payload: {
          data: {
            actions,
          },
        },
        cartId: _.get(newCart, 'id'),
      })
      return data
    } catch (error) {
      throw error
    }
  }

  async function handleCreateIsolateCart(options = {}) {
    const {
      cartId,
      checksum,
      payload,
      commerceChannel,
      type,
      orderMethodMandatory,
    } = options
    try {
      const { cart: isolateCart } = await createIsolateCart({
        cartId,
        checksum,
        payload,
        commerceChannel,
        type,
        orderMethodMandatory,
      })
      const data = await handleFetchIsolateCart({
        ..._.omit(options, ['payload', 'params', 'cartId']),
        ..._.get(options, 'params', {}),
        cartId: _.get(isolateCart, 'id'),
      })
      return data
    } catch (error) {
      throw error
    }
  }
  async function handleFetchIsolateCart(options = {}) {
    try {
      const data = await handleFetchCart({
        ...options,
        updateCartId: false,
      })
      return data
    } catch (error) {
      throw error
    }
  }

  async function handleFetchCart(options) {
    const {
      cartId,
      commerceChannel,
      updateCartId = true,
      ...params
    } = options
    try {
      const data = await fetchCart({ cartId, commerceChannel, params })
      const newCartId = _.get(data, 'cart.id')
      const cc = _.get(data, 'commerceChannel', '')

      if (updateCartId) {
        setCartIds((prevCartIds) => {
          return {
            ...prevCartIds,
            [cc]: newCartId,
          }
        })
      }
      return data
    } catch (error) {
      // ignore error if api call is canceled
      if (!error.isCancel) {
        setCartIds((prevCartIds) => _.omitBy(prevCartIds, (val) => _.isEqual(val, cartId)))
      }
      throw error
    }
  }

  function handleMergeCart(options) {
    const { cartId, ...params } = options
    const payload = {
      data: {
        cart: {
          merging_cart_id: cartId
        }
      }
    }
    return handleCreateCart({ payload, ...params })
  }

  async function handleUpdateCart(options) {
    const {
      params,
    } = options
    try {
      await updateCart({
        ...options,
        params: _.omit(params, ['includes']),
      })
      const data = await handleFetchCart({
        ..._.omit(options, ['payload', 'params']),
        ...params,
      })

      return data
    } catch (error) {
      throw error
    }
  }

  async function handleUpdateIsolateCart(options) {
    const {
      params,
    } = options
    try {
      await updateCart({
        ...options,
        params: _.omit(params, ['includes']),
      })
      const data = await handleFetchIsolateCart({
        ..._.omit(options, ['payload', 'params']),
        ...params,
      })

      return data
    } catch (error) {
      throw error
    }
  }

  const contextValue = {
    cartIds,
    reset: handleResetCart,
    // below methods are directly call API
    checkout,
    createCart: handleCreateCart,
    createIsolateCart: handleCreateIsolateCart,
    fetchCart: handleFetchCart,
    fetchIsolateCart: handleFetchIsolateCart,
    mergeCart: handleMergeCart,
    updateCart: handleUpdateCart,
    updateIsolateCart: handleUpdateIsolateCart
  }
  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  )
}
